import * as React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Formik } from 'formik';
import { toast } from 'react-toastify';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import SmsRoundedIcon from '@mui/icons-material/SmsRounded';
import ForwardToInboxRoundedIcon from '@mui/icons-material/ForwardToInboxRounded';
import { sendPaymentReceipt } from '../../../services/payments';
import {
  getGuestReceiptContactSchema,
  GuestReceiptContactValues,
} from '../../../types/payments';
import { Medium, Mediums } from '../../../types/global';
import ContactEmailField from './ContactEmailField';
import ContactPhoneField from './ContactPhoneField';

interface Props {
  paymentId: string;
  mediums: Medium[];
  initialEmail?: string | null;
  initialPhone?: string | null;
  onSubmittingChange: (submitting: boolean) => void;
  onSuccess: () => void;
}

function normalizePhoneForInput(phone?: string | null) {
  if (!phone) return '';
  return phone.replace(/\D/g, '');
}

export default function ContactStep({
  paymentId,
  mediums,
  initialEmail,
  initialPhone,
  onSubmittingChange,
  onSuccess,
}: Props) {
  const wantsEmail = mediums.includes(Mediums.EMAIL);
  const wantsSms = mediums.includes(Mediums.SMS);
  const wantsBoth = wantsEmail && wantsSms;

  const initialValues = React.useMemo<GuestReceiptContactValues>(
    () => ({
      email: initialEmail ?? '',
      phone: normalizePhoneForInput(initialPhone),
    }),
    [initialEmail, initialPhone],
  );

  const title = wantsBoth
    ? 'Text & email receipt'
    : wantsEmail
    ? 'Email receipt'
    : 'Text receipt';
  const subtitle = wantsBoth
    ? 'Where should we send the receipt?'
    : wantsEmail
    ? 'Type the email address to send the receipt to.'
    : 'Type the phone number to text the receipt to.';
  const HeaderIcon = wantsBoth
    ? ForwardToInboxRoundedIcon
    : wantsEmail
    ? EmailRoundedIcon
    : SmsRoundedIcon;

  return (
    <Stack alignItems="center" spacing={1.5} sx={{ width: '100%' }}>
      <Stack alignItems="center" spacing={0.5} sx={{ width: '100%' }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '14px',
            backgroundColor: '#FFD6CE',
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <HeaderIcon sx={{ fontSize: 24 }} />
        </Box>
        <Typography
          variant="h3"
          sx={{
            fontFamily: 'Montserrat, sans-serif',
            letterSpacing: '-0.01em',
            color: 'text.primary',
            textAlign: 'center',
            lineHeight: 1.2,
          }}>
          {title}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            textAlign: 'center',
            maxWidth: 320,
            lineHeight: 1.35,
          }}>
          {subtitle}
        </Typography>
      </Stack>

      <Formik<GuestReceiptContactValues>
        initialValues={initialValues}
        enableReinitialize
        validationSchema={getGuestReceiptContactSchema(mediums)}
        validateOnMount
        onSubmit={async (values, { setSubmitting }) => {
          onSubmittingChange(true);
          try {
            const phone =
              values.phone && !values.phone.startsWith('+')
                ? `+${values.phone}`
                : values.phone;
            await sendPaymentReceipt(
              paymentId,
              mediums,
              wantsEmail ? values.email : undefined,
              wantsSms ? phone : undefined,
            );
            onSuccess();
          } catch (err: any) {
            toast.error(err?.message || 'Failed to send receipt');
            onSubmittingChange(false);
          } finally {
            setSubmitting(false);
          }
        }}>
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          isValid,
        }) => (
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
              padding: 0,
              margin: 0,
              gap: 0,
              display: 'block',
            }}>
            <Stack spacing={1} sx={{ width: '100%' }}>
              {wantsSms && (
                <ContactPhoneField
                  name="phone"
                  autoFocus
                  disabled={isSubmitting}
                />
              )}
              {wantsEmail && (
                <ContactEmailField
                  name="email"
                  value={values.email}
                  error={touched.email ? errors.email ?? null : null}
                  autoFocus={!wantsSms}
                  disabled={isSubmitting}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              )}
            </Stack>

            <LoadingButton
              type="submit"
              loading={isSubmitting}
              disabled={!isValid}
              variant="contained"
              color="primary"
              className="primary"
              fullWidth
              sx={{
                mt: 1,
                height: 44,
                borderRadius: '10px',
                fontSize: 'body1.fontSize',
                textTransform: 'none',
              }}>
              Send receipt
            </LoadingButton>
          </Box>
        )}
      </Formik>
    </Stack>
  );
}
